import React, { useState, useEffect } from "react";
import { getRepairs, markPaymentAsCompleted } from "../../services/api";
import generateInvoiceContent from "../../pages/Sales/InvoicePrintTemplate"; // Import invoice template
import CreateRepairModal from "../../pages/Repairs/CreateRepair";
import CompleteRepairModal from "../../pages/Repairs/CompleteRepair";
import UpdatePartsModal from "../../pages/Repairs/UpdateParts";
import {
    AiOutlineTool,
    AiOutlineCheckCircle,
    AiOutlineDollarCircle,
    AiOutlinePlus,
    AiOutlineSearch,
    AiOutlineEye,
    AiOutlineLeft,
    AiOutlineRight
} from "react-icons/ai";
import styles from "../../styles/Repairs.module.css";

const Repairs = () => {
    const [repairs, setRepairs] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedRepair, setSelectedRepair] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeDropdown, setActiveDropdown] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
// Add this state in your Repairs component
    const [showActionsModal, setShowActionsModal] = useState(false);

// Add these handler functions
    const handleUpdate = (repairId) => {
        setSelectedRepair(repairId);
        setShowUpdateModal(true);
    };

    const handleComplete = (repairId) => {
        setSelectedRepair(repairId);
        setShowCompleteModal(true);
    };
    useEffect(() => {
        fetchRepairs();

        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (activeDropdown && !event.target.closest(`.${styles["repair-dropdown"]}`)) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeDropdown]);

    const fetchRepairs = async () => {
        try {
            const response = await getRepairs();
            setRepairs(response.data);
        } catch (error) {
            console.error("Error fetching repairs", error);
        }
    };

    const filteredRepairs = repairs.filter(
        (repair) =>
            repair.serial_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
            repair.CustomerNIC.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Calculate pagination
    const totalPages = Math.ceil(filteredRepairs.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentRepairs = filteredRepairs.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const getStatusClassName = (status) => {
        if (status === "Completed") return styles["repair-status-completed"];
        if (status === "Pending") return styles["repair-status-pending"];
        if (status === "Cancelled") return styles["repair-status-cancelled"];
        return "";
    };

    const handleMarkAsPaid = async (repair) => {
        console.log("Marking as paid for Repair ID:", repair.repair_id); // Debugging log

        if (!repair.id) {
            console.error("Error: Repair ID is undefined!");
            return;
        }

        try {
            await markPaymentAsCompleted(repair.id);
            fetchRepairs(); // Refresh repairs list
            openInvoicePrintPreview(repair); // Print invoice after marking as paid
            setActiveDropdown(null); // Close dropdown
        } catch (error) {
            console.error("Error marking payment as completed", error);
        }
    };


// Function to generate invoice print preview
    const openInvoicePrintPreview = (repair) => {
        const invoiceContent = generateInvoiceContent({
            invoiceTitle: 'Repair Invoice',
            invoiceNo: repair.id,
            date: new Date().toLocaleDateString(),
            salesRepresentative: "System Generated",
            customerName: repair.CustomerName || "Unknown",
            customerAddress: "N/A",
            paymentMethod: "Paid in Cash",
            items: repair.parts.map((part) => ({
                name: part.product_name,
                quantity: part.quantity,
                unitPrice: part.price,
                totalPrice: (part.quantity * part.price).toFixed(2),
            })),
            total: repair.total_parts_cost.toFixed(2),
            discount: "0.00",
            grandTotal: repair.total_amount.toFixed(2),
            notes: `Labor Charge: Rs.${repair.labor_charge.toFixed(2)}`,
        });

        const newWindow = window.open();
        newWindow.document.write(invoiceContent);
        newWindow.document.close();
        newWindow.print();
    };

    const handleViewDetails = (repair) => {
        setSelectedRepair(repair);
        setShowViewModal(true);
    };

    const toggleDropdown = (repairId) => {
        setActiveDropdown(activeDropdown === repairId ? null : repairId);
    };

    // Generate pagination numbers
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 2) {
                endPage = Math.min(4, totalPages - 1);
            }

            if (currentPage >= totalPages - 1) {
                startPage = Math.max(2, totalPages - 3);
            }

            if (startPage > 2) {
                pageNumbers.push('...');
            }

            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }

            if (endPage < totalPages - 1) {
                pageNumbers.push('...');
            }

            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };
// Add this modal component to your Repairs component

    const ActionsModal = ({ repair, onClose, onUpdate, onComplete, onMarkPaid }) => {
        if (!repair) return null;

        return (
            <div className={styles["repair-modal-overlay"]}>
                <div className={styles["repair-modal"]} style={{ maxWidth: "500px" }}>
                    <div className={styles["repair-modal-header"]}>
                        <h3>Repair Actions</h3>
                        <button onClick={onClose} className={styles["repair-modal-close"]}>
                            ✕
                        </button>
                    </div>
                    <div className={styles["repair-modal-content"]}>
                        <div className={styles["repair-detail-row"]}>
                            <strong>Serial Number:</strong> {repair.serial_number}
                        </div>

                        <div className={styles["repair-actions-list"]}>
                            {repair.repair_status !== "Completed" && (
                                <>
                                    <button
                                        className={styles["repair-modal-action-button"]}
                                        onClick={() => {
                                            onUpdate(repair.id);
                                            onClose();
                                        }}
                                        onBlur={(e) => {
                                            // Prevent closing if clicking inside the dropdown content
                                            if (!e.currentTarget.contains(e.relatedTarget)) {
                                                // Use a slight delay to allow click events to register on dropdown items
                                                setTimeout(() => setActiveDropdown(null), 100);
                                            }
                                        }}
                                    >
                                        <AiOutlineTool className={styles["repair-action-icon"]} />
                                        Update Parts
                                    </button>

                                    <button
                                        className={styles["repair-modal-action-button"]}
                                        onClick={() => {
                                            onComplete(repair.id);
                                            onClose();
                                        }}
                                    >
                                        <AiOutlineCheckCircle className={styles["repair-action-icon"]} />
                                        Complete Repair
                                    </button>
                                </>
                            )}

                            {repair.payment_status !== "Completed" && (
                                <button
                                    onClick={() => handleMarkAsPaid(repair)}
                                    className={styles["repair-action-button"]}
                                >
                                    Mark as Paid
                                </button>
                            )}
                        </div>
                    </div>
                    <div className={styles["repair-modal-footer"]}>
                        <button onClick={onClose} className={styles["repair-close-button"]}>Cancel</button>
                    </div>
                </div>
            </div>
        );
    };
    const ViewDetailsModal = ({ repair, onClose }) => {
        if (!repair) return null;

        return (
            <div className={styles["repair-modal-overlay"]}>
                <div className={styles["repair-modal"]}>
                    <div className={styles["repair-modal-header"]}>
                        <h3>Repair Details</h3>
                        <button onClick={onClose} className={styles["repair-modal-close"]}>
                            ✕
                        </button>
                    </div>
                    <div className={styles["repair-modal-content"]}>
                        {/* First Table: Repair Information */}
                        <table className={styles["repair-details-table"]}>
                            <tbody>
                            <tr>
                                <td className={styles["repair-detail-label"]}>Serial Number:</td>
                                <td>{repair.serial_number}</td>
                            </tr>
                            <tr>
                                <td className={styles["repair-detail-label"]}>Customer:</td>
                                <td>{repair.CustomerNIC || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className={styles["repair-detail-label"]}>Status:</td>
                                <td><div className={getStatusClassName(repair.repair_status)}>{repair.repair_status}</div></td>
                            </tr>
                            <tr>
                                <td className={styles["repair-detail-label"]}>Payment Status:</td>
                                <td><div className={getStatusClassName(repair.payment_status)}>{repair.payment_status}</div></td>
                            </tr>
                            </tbody>
                        </table>

                        <h4 className={styles["repair-detail-section"]}>Cost Details</h4>

                        {/* Second Table: Cost Information */}
                        <table className={styles["repair-details-table"]}>
                            <tbody>
                            <tr>
                                <td className={styles["repair-detail-label"]}>Parts Cost:</td>
                                <td>Rs.{Number(repair.total_parts_cost || 0).toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td className={styles["repair-detail-label"]}>Labor Charge:</td>
                                <td>Rs.{Number(repair.labor_charge || 0).toFixed(2)}</td>
                            </tr>
                            <tr className={styles["repair-total-row"]}>
                                <td className={styles["repair-detail-label"]}>Total Amount:</td>
                                <td>Rs.{Number(repair.total_amount || 0).toFixed(2)}</td>
                            </tr>
                            </tbody>
                        </table>

                        {repair.parts && repair.parts.length > 0 && (
                            <>
                                <h4 className={styles["repair-detail-section"]}>Parts Used</h4>
                                <table className={styles["repair-parts-table"]}>
                                    <thead>
                                    <tr>
                                        <th>Part Name</th>
                                        <th>Quantity</th>
                                        <th>Unit Price</th>
                                        <th>Total</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {repair.parts.map((part, index) => (
                                        <tr key={index}>
                                            <td>{part.product_name || "Unknown Part"}</td>
                                            <td>{part.quantity}</td>
                                            <td>Rs.{Number(part.price || 0).toFixed(2)}</td>
                                            <td>Rs.{Number((part.quantity * part.price) || 0).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {repair.notes && (
                            <div className={styles["repair-detail-notes"]}>
                                <strong>Notes:</strong>
                                <p>{repair.notes}</p>
                            </div>
                        )}
                    </div>
                    <div className={styles["repair-modal-footer"]}>
                        <button onClick={onClose} className={styles["repair-close-button"]}>Close</button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles["repair-content"]}>
            {/* Search bar and Add Button aligned side by side */}
            <div className={styles["repair-searchContainer"]}>
                <div className={styles["repair-searchBar"]}>
                    <AiOutlineSearch className={styles["repair-searchIcon"]} />
                    <input
                        type="text"
                        placeholder="Search by Serial Number or Customer NIC..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={styles["repair-searchInput"]}
                    />
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className={styles["repair-addButton"]}
                >
                    <AiOutlinePlus /> Add New Repair
                </button>
            </div>

            {/* Repairs Table */}
            <div className={styles["repair-section"]}>
                <table className={styles["repair-table"]}>
                    <thead>
                    <tr>
                        <th>Serial Number</th>
                        <th>Status</th>
                        <th>Customer NIC</th>
                        <th>Total Parts Cost</th>
                        <th>Labor Charge</th>
                        <th>Total Amount</th>
                        <th>Payment Status</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentRepairs.map((repair) => (
                        <tr key={repair.id}>
                            <td>{repair.serial_number}</td>
                            <td>
                                <div className={getStatusClassName(repair.repair_status)}>
                                    {repair.repair_status}
                                </div>
                            </td>
                            <td>{repair.CustomerNIC || "N/A"}</td>
                            <td>Rs.{Number(repair.total_parts_cost || 0).toFixed(2)}</td>
                            <td>Rs.{Number(repair.labor_charge || 0).toFixed(2)}</td>
                            <td>Rs.{Number(repair.total_amount || 0).toFixed(2)}</td>
                            <td>
                                <div className={getStatusClassName(repair.payment_status)}>
                                    {repair.payment_status}
                                </div>
                            </td>
                            <td className={styles["repair-actionButtons"]}>
                                {/* For completed repairs with completed payment, show View Details */}
                                {repair.repair_status === "Completed" && repair.payment_status === "Completed" && (
                                    <button
                                        onClick={() => handleViewDetails(repair)}
                                        className={styles["repair-view-button"]}
                                    >
                                        View Details
                                    </button>
                                )}

                                {/* For repairs with pending status, show the action buttons */}
                                {repair.repair_status !== "Completed" && repair.payment_status !== "Completed" && (
                                    // Display action button that opens a modal with all options
                                    <button
                                        onClick={() => {
                                            setSelectedRepair(repair);
                                            setShowActionsModal(true);
                                        }}
                                        className={styles["repair-action-button"]}
                                    >
                                        Actions
                                    </button>
                                )}

                                {/* For completed repairs with pending payment, show mark as paid */}
                                {repair.repair_status === "Completed" && repair.payment_status !== "Completed" && (
                                    <button
                                        onClick={() => handleMarkAsPaid(repair)}
                                        className={styles["repair-action-button"]}
                                    >
                                        Mark as Paid
                                    </button>
                                )}

                                {/* For pending repairs with completed payment, show update/complete */}
                                {repair.repair_status !== "Completed" && repair.payment_status === "Completed" && (
                                    <button
                                        onClick={() => {
                                            setSelectedRepair(repair);
                                            setShowActionsModal(true);
                                        }}
                                        className={styles["repair-action-button"]}
                                    >
                                        Actions
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className={styles["repair-pagination"]}>
                    <button
                        className={styles["repair-pagination-button"]}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <AiOutlineLeft /> Prev
                    </button>

                    <div className={styles["repair-pagination-numbers"]}>
                        {getPageNumbers().map((page, index) => (
                            page === '...' ? (
                                <span key={`ellipsis-${index}`} className={styles["repair-pagination-ellipsis"]}>...</span>
                            ) : (
                                <button
                                    key={page}
                                    className={`${styles["repair-pagination-number"]} ${currentPage === page ? styles["repair-pagination-active"] : ''}`}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </button>
                            )
                        ))}
                    </div>

                    <button
                        className={styles["repair-pagination-button"]}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next <AiOutlineRight />
                    </button>
                </div>
            )}

            <div className={styles["repair-pagination-info"]}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredRepairs.length)} of {filteredRepairs.length} entries
            </div>

            {/* Modals for Repairs */}
            {showCreateModal && (
                <CreateRepairModal
                    onClose={() => setShowCreateModal(false)}
                    onRepairCreated={fetchRepairs}
                    className={styles["repair-modal"]}
                />
            )}
            {showActionsModal && selectedRepair && (
                <ActionsModal
                    repair={selectedRepair}
                    onClose={() => {
                        setShowActionsModal(false);
                        setSelectedRepair(null);
                    }}
                    onUpdate={handleUpdate}
                    onComplete={handleComplete}
                    onMarkPaid={handleMarkAsPaid}
                />
            )}
            {showCompleteModal && selectedRepair && (
                <CompleteRepairModal
                    repairId={selectedRepair?.id} // ✅ Pass only repair.id
                    onClose={() => setShowCompleteModal(false)}
                    onRepairCompleted={fetchRepairs}
                />

            )}

            {showUpdateModal && selectedRepair && (
                <UpdatePartsModal
                    repairId={selectedRepair?.id} // ✅ Pass only repair.id
                    onClose={() => setShowUpdateModal(false)}
                    onPartsUpdated={fetchRepairs}
                />

            )}

            {showViewModal && selectedRepair && (
                <ViewDetailsModal
                    repair={selectedRepair}
                    onClose={() => {
                        setShowViewModal(false);
                        setSelectedRepair(null);
                    }}
                />
            )}
        </div>
    );
};

export default Repairs;